/* ModalComponent component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, State, Method, Prop, Event, EventEmitter, h, Listen, Watch } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import { AbstractComponent, Slot } from '@cewe-designsystem/abstract';
import ModalTemplate from './cwc-modal.template';
import UtilBreakpoints from '@cewe-designsystem/util_breakpoints';
import '../../../behaviors/exit-intent';

/**
 * @slot close-icon - HTML element used as caption for the button to close the modal e.g. an img or svg
 * @slot header-items - HTML elements used as header content
 * @slot footer-items - HTML elements used as footer content
 */
@Component({
  tag: 'cwc-component-modal',
  styleUrl: './cwc-modal.scss',
  shadow: true,
})
export class ModalComponent {
  dialogRef: HTMLDialogElement;
  dialogBodyRef: HTMLDivElement;
  private dialogObserver: MutationObserver;
  _breakpointHandler: UtilBreakpoints = new UtilBreakpoints();
  private clickHandlerBlocked: boolean = false;

  /**
   * Slots
   */
  @Slot('header-items') headerItems;
  @Slot('footer-items') footerItems;

  /**
   * Force enable modals fullscreen mode
   *
   * @docs
   * @property {boolean} fullscreen
   */
  @Prop({ reflect: true }) fullscreen: boolean = false;
  /**
   * Open dialog immediately
   *
   * @docs
   * @property {boolean} opened
   */
  @Prop({ reflect: true, mutable: true }) opened: boolean = false;
  /**
   * Breakpoint to use for switching between fullscreen and non-fullscreen mode
   *
   * @docs
   * @property {string} fullScreenBreakpoint
   */
  @Prop() fullScreenBreakpoint: string = 'sm';
  /**
   * Adapt styling (i.e. padding) of cwc-modal__header for centered or left aligned content
   *
   * @docs
   * @property {string} headerTextAlign
   */
  @Prop() headerTextAlign: string = 'center';
  /**
   * restrict content width of modal header, modal body and modal footer to display approx. 80 characters per line
   *
   * @docs
   * @property {boolean} narrow
   */
  @Prop() narrow: boolean = false;

  @Watch('fullscreen')
  onFullScreenAttributeChanged(newValue: boolean) {
    this._fullscreen = newValue || this._breakpointHandler.breakpointDown(this.fullScreenBreakpoint);
  }

  @Watch('opened')
  onOpenedAttributeChanged(newValue: boolean) {
    newValue ? this.onOpen() : this.onClose();
  }

  @State() _fullscreen = this.fullscreen || this._breakpointHandler.breakpointDown(this.fullScreenBreakpoint);

  /**
   * element
   */
  @Element()
  element: HTMLElement;

  /**
   * Event thrown if the modal is opened
   *
   * @docs
   * @event on:modal:open
   */
  @Event({
    eventName: 'on:modal:open',
  })
  onModalOpen: EventEmitter;

  /**
   * Event thrown if the modal is closed
   *
   * @docs
   * @event on:modal:close
   */
  @Event({
    eventName: 'on:modal:close',
  })
  onModalClose: EventEmitter;

  /**
   * Listening on changed breakpoints (on:breakpoint:change)
   */
  @Listen('on:breakpoint:changed', { target: 'window' })
  handleResize() {
    this._fullscreen = this.fullscreen || this._breakpointHandler.breakpointDown(this.fullScreenBreakpoint);
  }

  /**
   * Detects Elements with attribute data-close-modal and registers click event listeners to close the modal
   * */
  @Listen('click')
  closeButtonClickListener(event: MouseEvent): void {
    const closeModal: boolean = event.composedPath().some((target: EventTarget) => {
      return target instanceof HTMLElement && target.hasAttribute('data-close-modal');
    });
    if (closeModal) {
      this.onClose();
    }
  }

  onOpen() {
    this.dialogRef?.showModal();
    this.dialogBodyRef?.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  }

  onClose() {
    this.dialogRef?.close();
  }

  handleMouseDown(event: MouseEvent): void {
    // block handler, when click did not start on the backdrop, but ends on it
    this.clickHandlerBlocked = (event.target as HTMLElement).tagName.toLowerCase() !== 'dialog';
  }

  handleClick(event: MouseEvent) {
    // handle backdrop click, only works properly when dialog element itself does not have any padding or border
    if (!this.clickHandlerBlocked && (event.target as HTMLElement).tagName.toLowerCase() === 'dialog') {
      this.onClose();
    }
    this.clickHandlerBlocked = false;
  }

  /**
   * Open the modal
   *
   * @function open
   * @docs
   */
  @Method()
  async open() {
    this.onOpen();
  }

  /**
   * Close the modal
   *
   * @function close
   * @docs
   */
  @Method()
  async close() {
    this.onClose();
  }

  /**
   * @inheritDoc
   */
  componentDidLoad() {
    this.setupDialogRefMutationObserver();
    if (this.opened) {
      this.onOpen();
    }
  }

  /**
   * Sets up MutationObserver for dialog
   *
   * @function setupDialogRefMutationObserver
   */
  private setupDialogRefMutationObserver(): void {
    this.dialogObserver = new MutationObserver(this.dialogRefMutationObserverCallback.bind(this));
    this.dialogObserver.observe(this.dialogRef, { attributes: true });
  }

  /**
   * Checks for changes to the open attribute of the internal HTML dialog element
   *
   * @function dialogRefMutationObserverCallback
   */
  private dialogRefMutationObserverCallback(mutations: MutationRecord[]): void {
    // detect state and emit events
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
        this.opened = (mutation.target as HTMLDialogElement).hasAttribute('open');
        this.opened ? this.onModalOpen.emit(this.element) : this.onModalClose.emit(this.element);
      }
    }
  }

  /**
   * @inheritDoc
   */
  render() {
    return (
      <ModalTemplate
        controller={this}
        fullscreen={this._fullscreen}
        headerTextAlign={this.headerTextAlign}
        narrow={this.narrow}
      />
    );
  }

  /**
   * @inheritDoc
   */
  disconnectedCallback() {
    this.dialogObserver?.disconnect();
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(ModalComponent.prototype, AbstractComponent.prototype);

export interface ModalComponent extends AbstractComponent {}
